import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemText,
  Divider,
  Chip,
  CircularProgress,
  IconButton,
  Button,
} from '@mui/material';
import { Delete as DeleteIcon, CheckCircle as CheckCircleIcon } from '@mui/icons-material';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { getNotifications, markNotificationRead, markAllRead, deleteNotification } from '../services/api';

const NotificationItem = ({ notification, onDelete, onMarkRead }) => {
  const navigate = useNavigate();

  const handleClick = () => {
    if (!notification.read) {
      onMarkRead(notification.id);
    }
    if (notification.project_id) {
      navigate(`/projects/${notification.project_id}`);
    }
  };

  return (
    <>
      <ListItem
        button={notification.project_id ? true : false}
        onClick={handleClick}
        sx={{
          bgcolor: notification.read ? 'inherit' : 'action.hover',
          '&:hover': { bgcolor: 'action.selected' },
        }}
        secondaryAction={
          <IconButton edge="end" onClick={() => onDelete(notification.id)}>
            <DeleteIcon />
          </IconButton>
        }
      >
        <ListItemText
          primary={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body1" fontWeight={notification.read ? 'normal' : 'bold'}>
                {notification.message}
              </Typography>
              {!notification.read && <Chip label="New" size="small" color="primary" />}
            </Box>
          }
          secondary={
            <Box sx={{ mt: 0.5 }}>
              {notification.project_name && (
                <Typography variant="caption" display="block">
                  Project: {notification.project_name}
                </Typography>
              )}
              <Typography variant="caption" color="text.secondary">
                {format(new Date(notification.created_at), 'MMM d, yyyy h:mm a')}
              </Typography>
            </Box>
          }
        />
      </ListItem>
      <Divider component="li" />
    </>
  );
};

const Notifications = () => {
  const [loading, setLoading] = useState(true);
  const [notifications, setNotifications] = useState([]);

  useEffect(() => {
    loadNotifications();
  }, []);

  const loadNotifications = async () => {
    setLoading(true);
    try {
      const response = await getNotifications();
      setNotifications(response.data);
    } catch (error) {
      console.error('Failed to load notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkRead = async (id) => {
    try {
      await markNotificationRead(id);
      await loadNotifications();
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await markAllRead();
      await loadNotifications();
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteNotification(id);
      await loadNotifications();
    } catch (error) {
      console.error('Failed to delete notification:', error);
    }
  };

  const unreadCount = notifications.filter((n) => !n.read).length;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Notifications
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {unreadCount} unread notifications
          </Typography>
        </Box>
        {unreadCount > 0 && (
          <Button
            variant="outlined"
            startIcon={<CheckCircleIcon />}
            onClick={handleMarkAllRead}
          >
            Mark All Read
          </Button>
        )}
      </Box>

      <Paper elevation={2}>
        {notifications.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">No notifications</Typography>
          </Box>
        ) : (
          <List>
            {notifications.map((notification) => (
              <NotificationItem
                key={notification.id}
                notification={notification}
                onDelete={handleDelete}
                onMarkRead={handleMarkRead}
              />
            ))}
          </List>
        )}
      </Paper>
    </Box>
  );
};

export default Notifications;
